// ╔══════════════════════════════════════════════════════════════╗
// ║  IdleTimeout — logs out user after 5 min of inactivity        ║
// ║  Shows a warning modal 1 minute before logout                 ║
// ╚══════════════════════════════════════════════════════════════╝

import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../lib/auth.jsx';
import { Btn, Modal } from './ui.jsx';
import Icon from './Icon.jsx';

const IDLE_LIMIT_MS = 5 * 60 * 1000; // 5 minutes
const WARN_BEFORE_MS = 60 * 1000; // 1 minute before logout

const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll', 'mousemove'];

export default function IdleTimeout() {
  const { user, logout } = useAuth();
  const [warning, setWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(60);
  const lastActivity = useRef(Date.now());
  const checkInterval = useRef(null);
  const countdownInterval = useRef(null);

  useEffect(() => {
    if (!user) return undefined;

    function recordActivity() {
      lastActivity.current = Date.now();
      if (warning) {
        // user came back during warning — dismiss it
        setWarning(false);
      }
    }

    // Throttle mousemove to once per second
    let lastMouseMove = 0;
    function throttledActivity(e) {
      if (e.type === 'mousemove') {
        const now = Date.now();
        if (now - lastMouseMove < 1000) return;
        lastMouseMove = now;
      }
      recordActivity();
    }

    ACTIVITY_EVENTS.forEach((ev) =>
      window.addEventListener(ev, throttledActivity, { passive: true }),
    );

    // Check every 10 seconds whether the user has been idle
    checkInterval.current = setInterval(() => {
      const elapsed = Date.now() - lastActivity.current;
      if (elapsed >= IDLE_LIMIT_MS) {
        // time's up
        clearInterval(checkInterval.current);
        clearInterval(countdownInterval.current);
        logout();
      } else if (elapsed >= IDLE_LIMIT_MS - WARN_BEFORE_MS && !warning) {
        setWarning(true);
      }
    }, 10_000);

    return () => {
      ACTIVITY_EVENTS.forEach((ev) =>
        window.removeEventListener(ev, throttledActivity),
      );
      if (checkInterval.current) clearInterval(checkInterval.current);
      if (countdownInterval.current) clearInterval(countdownInterval.current);
    };
  }, [user, warning, logout]);

  // Countdown timer while warning is shown
  useEffect(() => {
    if (!warning) return undefined;
    function tick() {
      const remainingMs = IDLE_LIMIT_MS - (Date.now() - lastActivity.current);
      const sec = Math.max(0, Math.ceil(remainingMs / 1000));
      setSecondsLeft(sec);
      if (sec <= 0) {
        clearInterval(countdownInterval.current);
        logout();
      }
    }
    tick();
    countdownInterval.current = setInterval(tick, 1000);
    return () => clearInterval(countdownInterval.current);
  }, [warning, logout]);

  if (!user || !warning) return null;

  function handleStay() {
    lastActivity.current = Date.now();
    setWarning(false);
  }

  return (
    <Modal
      open
      onClose={handleStay}
      title="انتهاء الجلسة قريباً"
      size="sm"
      footer={
        <>
          <Btn variant="ghost" onClick={logout}>تسجيل الخروج الآن</Btn>
          <Btn variant="primary" icon="check" onClick={handleStay}>
            متابعة الجلسة
          </Btn>
        </>
      }
    >
      <div className="text-center py-2">
        <div className="w-16 h-16 mx-auto rounded-full bg-amber-100 dark:bg-amber-950/30 text-amber-600 grid place-items-center mb-3">
          <Icon name="clock" size={32} />
        </div>
        <p className="text-sm">
          لم نلاحظ نشاطاً على الحساب منذ مدة. ستنتهي الجلسة تلقائياً بعد:
        </p>
        <div className="text-5xl font-black tabular text-brand-600 dark:text-brand-300 mt-3">
          {String(Math.floor(secondsLeft / 60)).padStart(2, '0')}:{String(secondsLeft % 60).padStart(2, '0')}
        </div>
        <p className="text-xs text-ink-400 mt-3">
          اضغط <strong>متابعة الجلسة</strong> لإلغاء تسجيل الخروج.
        </p>
      </div>
    </Modal>
  );
}
